import { useState, useEffect, useCallback, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import axios from "axios";
import { api } from "@/lib/api";
import { Select, Button } from "@/components/kit";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import HouseholdCheckinPrompt from "@/components/HouseholdCheckinPrompt";
import { RolePicker, RolePickerModal } from "@/components/checkin/RolePicker";

const pub = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

function StaffCheckIn({ onCheckedIn }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [visitor, setVisitor] = useState(null); // null | {name, organization, phone, email}
  const [vErr, setVErr] = useState("");
  const [vBusy, setVBusy] = useState(false);
  const [household, setHousehold] = useState(null);

  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(() => pub.get("/checkin/members", { params: { q } }).then((r) => setResults(r.data.members)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  const tapKey = (d) => { setErr(""); if (d === "del") setPin((p) => p.slice(0, -1)); else if (pin.length < 4) setPin((p) => p + d); };
  const reset = () => { setPicked(null); setPin(""); setQ(""); setResults([]); setErr(""); setVisitor(null); setVErr(""); setHousehold(null); };
  const finishSuccess = (n) => { setDone(n); setTimeout(() => { setDone(null); reset(); }, 1800); };
  const [roles, setRoles] = useState(null);
  const afterMember = (d) => {
    onCheckedIn && onCheckedIn();
    const hh = d.household || [];
    if (hh.length) setHousehold({ memberId: d.member_id, list: hh, name: d.name });
    else finishSuccess(d.name);
  };
  const submit = useCallback(async (fullPin) => {
    setErr("");
    try {
      const r = await pub.post("/checkin/member-by-id", { member_id: picked.member_id, pin: fullPin });
      if (r.data.needs_roles) { setRoles(r.data); return; }
      afterMember(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "That didn't work."); setPin(""); }
  }, [picked, onCheckedIn]);
  useEffect(() => { if (pin.length === 4 && picked) submit(pin); /* eslint-disable-next-line */ }, [pin]);

  const startVisitor = () => { setVisitor({ name: q.trim(), organization: "", phone: "", email: "" }); setVErr(""); };
  const submitVisitor = async () => {
    if (!visitor.name.trim()) { setVErr("Please enter a name."); return; }
    setVBusy(true);
    try {
      const r = await pub.post("/checkin/visitor/new", visitor);
      setDone(r.data.name); onCheckedIn && onCheckedIn();
      setTimeout(() => { setDone(null); reset(); }, 1800);
    } catch (e) { setVErr(e.response?.data?.detail || "Could not check in the visitor."); }
    finally { setVBusy(false); }
  };

  if (roles) {
    return <RolePicker data={roles} onDone={(d) => { setRoles(null); afterMember(d); }} onCancel={() => { setRoles(null); reset(); }} />;
  }
  if (done) {
    return (
      <div className="text-center py-6" data-testid="staff-checkin-success">
        <div className="w-12 h-12 rounded-full bg-olive/25 text-dark-olive flex items-center justify-center mx-auto mb-2 text-2xl">✓</div>
        <div className="font-zilla text-xl font-semibold">Checked in!</div>
        <div className="text-ink/70 text-[15px]">{done}</div>
      </div>
    );
  }
  if (household) {
    return (
      <div data-testid="staff-household">
        <div className="text-center mb-2"><div className="font-zilla text-lg font-semibold">{household.name} is checked in ✓</div></div>
        <HouseholdCheckinPrompt memberId={household.memberId} household={household.list}
          onDone={() => { setHousehold(null); finishSuccess(household.name); }} />
      </div>
    );
  }
  if (visitor) {
    const vbox = "w-full bg-white border border-ink/20 rounded-lg px-3 py-2.5 min-h-[46px] text-[15px]";
    return (
      <div data-testid="staff-visitor-form">
        <button data-testid="staff-visitor-back" onClick={() => { setVisitor(null); setVErr(""); }} className="text-dark-olive font-semibold text-[14px] mb-2">← Back</button>
        <div className="text-center mb-3"><div className="font-zilla text-lg font-semibold">Add a visitor</div><div className="text-ink/55 text-[13px]">No membership needed</div></div>
        <div className="space-y-2">
          <input data-testid="staff-visitor-name" value={visitor.name} onChange={(e) => setVisitor({ ...visitor, name: e.target.value })} placeholder="Visitor name" className={vbox} />
          <input data-testid="staff-visitor-org" value={visitor.organization} onChange={(e) => setVisitor({ ...visitor, organization: e.target.value })} placeholder="Organization / business (optional)" className={vbox} />
          <input data-testid="staff-visitor-phone" value={visitor.phone} onChange={(e) => setVisitor({ ...visitor, phone: e.target.value })} placeholder="Phone (optional)" className={vbox} />
          <input data-testid="staff-visitor-email" value={visitor.email} onChange={(e) => setVisitor({ ...visitor, email: e.target.value })} placeholder="Email (optional)" className={vbox} />
          {vErr && <div className="text-maroon text-center text-[14px]" data-testid="staff-visitor-error">{vErr}</div>}
          <Button variant="olive" data-testid="staff-visitor-submit" onClick={submitVisitor} disabled={vBusy} className="w-full min-h-[48px] text-[16px]">{vBusy ? "Checking in…" : "Check in visitor"}</Button>
        </div>
      </div>
    );
  }
  if (!picked) {
    return (
      <div>
        <input data-testid="staff-name-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a member name or number…" className="w-full bg-white border border-ink/20 rounded-lg px-3 py-2.5 min-h-[46px] text-[15px] mb-2" />
        <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
          {results.map((m) => (
            <button key={m.member_id} data-testid={`staff-name-option-${m.member_id}`} onClick={() => setPicked(m)} className="w-full text-left bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[46px] flex items-center justify-between">
              <span className="font-semibold text-[15px]">{m.name}</span>
              <span className="text-[12px] text-ink/45">{m.member_number}</span>
            </button>
          ))}
          {q.trim() && results.length === 0 && (
            <div className="text-center py-4" data-testid="staff-no-match">
              <div className="text-ink/55 text-[14px] mb-2">No match for &ldquo;{q.trim()}&rdquo;.</div>
              <Button variant="olive" data-testid="staff-add-visitor" onClick={startVisitor}><Icon name="UserPlus" className="w-4 h-4" /> Add as a visitor</Button>
            </div>
          )}
        </div>
        {!q.trim() && (
          <button data-testid="staff-add-visitor-cta" onClick={() => setVisitor({ name: "", organization: "", phone: "", email: "" })} className="block w-full text-center text-dark-olive font-semibold text-[14px] pt-3">Not a member? Add a visitor →</button>
        )}
      </div>
    );
  }
  return (
    <div>
      <button data-testid="staff-checkin-back" onClick={() => { setPicked(null); setPin(""); }} className="text-dark-olive font-semibold text-[14px] mb-2">← Back</button>
      <div className="text-center mb-2"><div className="font-zilla text-lg font-semibold">{picked.name}</div><div className="text-ink/55 text-[13px]">Enter 4-digit PIN</div></div>
      <div className="flex justify-center gap-2.5 mb-3" data-testid="staff-pin-dots">
        {[0, 1, 2, 3].map((i) => <div key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "bg-ink" : "border-2 border-ink/30"}`} />)}
      </div>
      {err && <div className="text-maroon text-center text-[14px] mb-2" data-testid="staff-pin-error">{err}</div>}
      <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} data-testid={`staff-key-${d}`} onClick={() => tapKey(d)} className="bg-warm-cream border border-ink/20 rounded-lg min-h-[52px] text-[20px] font-semibold">{d}</button>
        ))}
        <div />
        <button data-testid="staff-key-0" onClick={() => tapKey("0")} className="bg-warm-cream border border-ink/20 rounded-lg min-h-[52px] text-[20px] font-semibold">0</button>
        <button data-testid="staff-key-del" onClick={() => tapKey("del")} className="bg-warm-cream border border-ink/20 rounded-lg min-h-[52px] text-[16px] font-semibold">⌫</button>
      </div>
    </div>
  );
}

function ScanCheckIn({ onCheckedIn }) {
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [msg, setMsg] = useState(null);
  const [rolePick, setRolePick] = useState(null);
  const scannerRef = useRef(null);
  const regionId = "qr-scan-region";

  const doCheckin = useCallback(async (memberNumber) => {
    try {
      const r = await api.post("/checkin/scan", { member_number: memberNumber });
      if (r.data.needs_roles) { setRolePick(r.data); return; }
      setMsg({ ok: true, text: `${r.data.name} · ${r.data.member_number}` });
      setManual("");
      onCheckedIn && onCheckedIn();
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not check in" });
      setTimeout(() => setMsg(null), 3000);
    }
  }, [onCheckedIn]);

  const stop = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_) {}
      try { await scannerRef.current.clear(); } catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const start = async () => {
    setScanning(true); setMsg(null);
    try {
      const h = new Html5Qrcode(regionId);
      scannerRef.current = h;
      await h.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
        async (decoded) => { await stop(); await doCheckin((decoded || "").trim().toUpperCase()); },
        () => {});
    } catch (e) {
      setScanning(false);
      setMsg({ ok: false, text: "Camera unavailable — type the member number instead." });
    }
  };
  useEffect(() => () => { stop(); }, [stop]);

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="scan-checkin-card">
      <RolePickerModal data={rolePick} onClose={() => setRolePick(null)}
        onDone={(d) => { setRolePick(null); setMsg({ ok: true, text: `${d.name} · ${(d.roles_checked || []).join(", ")}` }); onCheckedIn && onCheckedIn(); setTimeout(() => setMsg(null), 3000); }} />
      <h3 className="font-zilla text-lg font-semibold mb-1">Scan a member's card</h3>
      <p className="text-[14px] text-ink/60 mb-3">Point the camera at the QR on a member's digital card to check them in — no PIN needed.</p>
      <div id={regionId} data-testid="qr-scan-region" className={`rounded-lg overflow-hidden mb-3 ${scanning ? "" : "hidden"}`} />
      {!scanning
        ? <Button data-testid="scan-start" onClick={start}><Icon name="QrCode" className="w-4 h-4" /> Start camera scan</Button>
        : <button data-testid="scan-stop" onClick={stop} className="border border-maroon/40 text-maroon rounded-lg px-4 py-2 min-h-[44px] text-[14px] font-semibold">Stop camera</button>}
      <div className="flex gap-2 mt-3">
        <input data-testid="scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && manual.trim() && doCheckin(manual.trim())} placeholder="or type member #, e.g. RCA-0249" className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]" />
        <button data-testid="scan-manual-submit" onClick={() => manual.trim() && doCheckin(manual.trim())} className="bg-ink text-cream rounded-lg px-4 min-h-[44px] text-[14px] font-semibold">Check in</button>
      </div>
      {msg && <div data-testid="scan-result" className={`mt-3 text-[14px] font-semibold ${msg.ok ? "text-dark-olive" : "text-maroon"}`}>{msg.ok ? "✓ Checked in: " : ""}{msg.text}</div>}
    </div>
  );
}

const timeAgo = (iso) => {  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

// Assign / reassign an event to a session — retroactively credits every attendee.
function AssignEvent({ session, events, onDone }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await api.post(`/checkin/sessions/${session.session_id}/assign-event`, { event_id: pick });
      toast.success("Session assigned — attendees credited");
      setPick(""); onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 flex-wrap items-center" data-testid={`assign-event-${session.session_id}`}>
      <Select data-testid={`assign-event-picker-${session.session_id}`} value={pick} onChange={(e) => setPick(e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px] flex-1 min-w-[160px]">
        <option value="">{session.event_id ? "Reassign to an event…" : "Assign to an event…"}</option>
        {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.title}</option>)}
      </Select>
      <button data-testid={`assign-event-save-${session.session_id}`} onClick={assign} disabled={busy || !pick} className="bg-dark-olive text-cream rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold disabled:opacity-50">{busy ? "Saving…" : (session.event_id ? "Reassign" : "Assign")}</button>
    </div>
  );
}

// Blocking gate shown before any check-in action exists on the screen.
function CheckInGate({ events, onStart }) {
  const [busy, setBusy] = useState(false);
  const go = async (eid) => { setBusy(true); try { await onStart(eid); } finally { setBusy(false); } };
  return (
    <div className="max-w-lg mx-auto" data-testid="checkin-gate">
      <div className="bg-cream border border-ink/15 rounded-xl p-5 sm:p-6">
        <h2 className="font-zilla text-2xl sm:text-3xl font-semibold mb-1">What is this check-in for?</h2>
        <p className="text-[15px] text-ink/65 mb-5">Pick the event this welcome-table session is for. Check-in stays closed until you choose — this makes sure every check-in is recorded and counted.</p>
        <div className="space-y-2 max-h-[46vh] overflow-y-auto mb-4">
          {events.length === 0 && <div className="text-ink/55 text-[14px] py-4 text-center" data-testid="gate-no-events">No published events yet — you can still log this as unassigned below.</div>}
          {events.map((e) => (
            <button key={e.event_id} data-testid={`gate-event-${e.event_id}`} disabled={busy} onClick={() => go(e.event_id)} className="w-full text-left bg-warm-cream border border-ink/15 rounded-xl px-4 py-3 min-h-[56px] flex items-center justify-between hover:border-dark-olive/40 disabled:opacity-50">
              <span className="font-semibold text-[16px]">{e.title}</span>
              <span className="text-[12px] text-ink/45 shrink-0 ml-3">{(e.start || "").slice(0, 10)}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-ink/10 pt-4">
          <p className="text-[13px] text-ink/55 mb-2">Not tied to an event?</p>
          <button data-testid="gate-unassigned" disabled={busy} onClick={() => go(null)} className="w-full border-2 border-dashed border-ink/30 text-ink rounded-xl px-4 py-3 min-h-[52px] text-[15px] font-semibold hover:border-ink/50 disabled:opacity-50">No specific event — log as unassigned</button>
          <p className="text-[12px] text-ink/45 mt-2">This still creates a real session. You can assign it to an event later and everyone gets credited retroactively.</p>
        </div>
      </div>
    </div>
  );
}

function RecentSessions({ events, refreshKey, onChanged }) {
  const [sessions, setSessions] = useState([]);
  const load = useCallback(() => { api.get("/checkin/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!sessions.length) return null;
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="recent-sessions">
      <h3 className="font-zilla text-lg font-semibold mb-3">Sessions &amp; retroactive assignment</h3>
      <p className="text-[13px] text-ink/55 mb-3">Assign an unassigned session to its event (or fix a wrong one). Everyone on it is credited retroactively.</p>
      <div className="space-y-3">
        {sessions.map((s) => (
          <div key={s.session_id} data-testid={`session-row-${s.session_id}`} className="border-b border-ink/8 last:border-0 pb-3 last:pb-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-semibold text-[15px]">{s.event_title || "Unassigned"}</span>
              {!s.event_id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/30 text-ink" data-testid={`session-unassigned-${s.session_id}`}>unassigned</span>}
              {s.status === "open" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-olive/20 text-ink">live</span>}
            </div>
            <div className="text-[12px] text-ink/45 mb-2">{(s.started_at || "").replace("T", " ").slice(0, 16)} · {s.count} checked in</div>
            <AssignEvent session={s} events={events} onDone={() => { load(); onChanged && onChanged(); }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CheckInStation() {
  const [session, setSession] = useState({ active: false });
  const [live, setLive] = useState([]);
  const [events, setEvents] = useState([]);
  const [isFs, setIsFs] = useState(false);
  const [sessRefresh, setSessRefresh] = useState(0);
  const rootRef = useRef(null);
  const timer = useRef(null);
  const checkinUrl = `${window.location.origin}/checkin`;

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await rootRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch (e) { toast.error("Full screen isn't available on this device."); }
  };
  const goBack = () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: "dashboard" } })); };

  const loadLive = useCallback(async () => {
    try { const r = await api.get("/checkin/live"); setSession(r.data.session); setLive(r.data.checkins); }
    catch (e) { /* keep last */ }
  }, []);

  useEffect(() => {
    api.get("/events", { params: { status: "published", limit: 100 } }).then((r) => setEvents(r.data.items || [])).catch(() => {});
    loadLive();
    timer.current = setInterval(loadLive, 3000);
    return () => clearInterval(timer.current);
  }, [loadLive]);

  const bump = () => setSessRefresh((k) => k + 1);
  const start = async (eventId) => {
    try { await api.post("/checkin/sessions/start", { event_id: eventId || null }); toast.success("Check-in started"); await loadLive(); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  const close = async () => {
    if (!session.session_id) return;
    try { await api.post(`/checkin/sessions/${session.session_id}/close`); toast.success("Check-in closed"); await loadLive(); bump(); }
    catch (e) { toast.error("Could not close"); }
  };

  return (
    <div ref={rootRef} data-testid="checkin-station-page" className={isFs ? "bg-warm-cream min-h-screen overflow-y-auto p-6 lg:p-8" : ""}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="checkin.title" screen="Check-in">Check-In</Txt></h1>
        <div className="flex items-center gap-2 no-print">
          <Button variant="secondary" data-testid="checkin-back-btn" onClick={goBack}><Icon name="ChevronLeft" className="w-4 h-4" /> Back</Button>
          <Button data-testid="checkin-fullscreen-btn" onClick={toggleFullscreen}><Icon name={isFs ? "Minimize2" : "Maximize2"} className="w-4 h-4" /> {isFs ? "Exit full screen" : "Full screen"}</Button>
        </div>
      </div>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="checkin.subtitle" screen="Check-in">Print the QR for the welcome table. People scan it with their own phone to check in.</Txt></p>

      {!session.active ? (
        <CheckInGate events={events.filter((e) => !e.is_past)} onStart={start} />
      ) : (
      <div className="grid lg:grid-cols-2 gap-5">
        {/* QR + session control */}
        <div className="space-y-5">
          <div className="bg-cream border border-ink/15 rounded-xl p-5 text-center print-area" data-testid="checkin-qr-card">
            <h3 className="font-zilla text-lg font-semibold mb-3">Shared check-in QR</h3>
            <div className="inline-block bg-white p-4 rounded-xl border border-ink/10">
              <QRCodeCanvas value={checkinUrl} size={200} data-testid="checkin-qr" />
            </div>
            <div className="text-[13px] text-ink/60 mt-3 break-all">{checkinUrl}</div>
            <button data-testid="print-qr" onClick={() => window.print()} className="mt-3 border border-ink/25 rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold inline-flex items-center gap-1.5"><Icon name="Download" className="w-4 h-4" /> Print QR</button>
          </div>

          {/* Staff-operated check-in (member search + PIN keypad) */}
          <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="staff-checkin-card">
            <h3 className="font-zilla text-lg font-semibold mb-1">Check people in on this device</h3>
            <p className="text-[14px] text-ink/60 mb-3">Search a member and enter their PIN to check them in right here. Recording to: {session.event_title || "Unassigned session"}.</p>
            <StaffCheckIn onCheckedIn={loadLive} />
          </div>

          <ScanCheckIn onCheckedIn={loadLive} />

          <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="session-control">
            <h3 className="font-zilla text-lg font-semibold mb-2">Check-in session</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[15px]" data-testid="session-active">
                <span className="w-2.5 h-2.5 rounded-full bg-olive animate-pulse" />
                <span className="font-semibold">Live:</span> {session.event_title || "Unassigned"}
                {!session.event_id && <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-gold/30 text-ink">unassigned</span>}
              </div>
              {!session.event_id && (
                <div data-testid="active-assign">
                  <p className="text-[13px] text-ink/60 mb-1.5">Tie this session to an event now, or leave it unassigned and assign later.</p>
                  <AssignEvent session={session} events={events} onDone={() => { loadLive(); bump(); }} />
                </div>
              )}
              {session.event_id && (
                <div data-testid="active-reassign">
                  <p className="text-[13px] text-ink/60 mb-1.5">Wrong event? Reassign it — attendees are re-credited.</p>
                  <AssignEvent session={session} events={events} onDone={() => { loadLive(); bump(); }} />
                </div>
              )}
              <button data-testid="close-session" onClick={close} className="border border-maroon/40 text-maroon rounded-lg px-4 py-2 min-h-[42px] text-[14px] font-semibold">Stop check-in</button>
            </div>
          </div>

          <RecentSessions events={events} refreshKey={sessRefresh} onChanged={loadLive} />
        </div>

        {/* Live list */}
        <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="live-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-zilla text-lg font-semibold">Just checked in</h3>
            <span className="text-[13px] text-ink/50 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-olive animate-pulse" /> live</span>
          </div>
          {live.length === 0 ? (
            <div className="text-center text-ink/55 py-10">Nobody yet. Check-ins appear here instantly.</div>
          ) : (
            <div className="space-y-1 max-h-[520px] overflow-y-auto">
              {live.map((c) => (
                <div key={c.checkin_id} data-testid={`live-row-${c.checkin_id}`} className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-ink/8 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name={c.kind === "member" ? "IdCard" : "UserPlus"} className={`w-4 h-4 shrink-0 ${c.kind === "member" ? "text-dark-olive" : "text-gold"}`} />
                    <span className="font-semibold truncate">{c.name}</span>
                    {c.member_number && <span className="text-[12px] text-ink/45">{c.member_number}</span>}
                    {c.source === "self" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-olive/15 border border-olive/30">self</span>}
                  </div>
                  <span className="text-[12px] text-ink/45 shrink-0">{timeAgo(c.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
